import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { upsertData } from "@/supabase/functions";

interface AddToAuditTableParams {
  uid: string;
  event: string;
  sub_event: string;
  type: string;
  general_name?: string | null;
  game_name: string;
  success: boolean;
  ip_address: string;
  timestamp: string;
  timestamp_dt: string;
  email: string;
  comment: string;
  referrer_uid?: string | null;
}

interface AddToReferralPaymentsParams {
  referral_from_uid: any;
  referral_to_uid: any;
  source_trx: string;
  referral_date: string;
  referral_type: string | null;
  referral_item_id: any;
  amount_paid: any;
  percentage_discount: any;
  referral_payment_status: string;
  referral_payment_trx: string;
  referral_payment_wallet: string;
  referral_payment_amount: number;
  referral_payment_multipart: string;
  ineligibility_reason: string;
  ineligibility_comment: string;
}

interface TransactionsParams {
  purchase_trx: string;
  trx_source_wallet: string;
  trx_dest_wallet: string;
  trx_amount: number;
  trx_chain: string;
  trx_coin: string;
  trx_status: string;
  trx_date_time: any;
  trx_type: string;
  trx_uid: string;
  trx_comment: string;
}

/**
 * Generates a random ID similar to Firebase document IDs.
 *
 * @returns {string} - The generated random ID.
 */
function generateRandomId(length = 20) {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * chars.length);
    result += chars[randomIndex];
  }
  return result;
}

export async function addToAuditTable(data: any) {
  try {
    if (!data) {
      throw new Error("Data not provided or invalid");
    }

    const docId = generateRandomId();

    try {
      if (Array.isArray(data)) {
        const copyOfDatas = [];
        for (let i = 0; i < data.length; i++) {
          const copyOfData = { ...data[i] };
          delete copyOfData.date_timestamp;
          delete copyOfData.firebase_timestamp;

          if (!copyOfData.id) {
            copyOfData.id = generateRandomId();
          }

          try {
            if (copyOfData.timestamp_dt && !copyOfData.created_at) {
              copyOfData.created_at = new Date(
                copyOfData.timestamp_dt * 1000
              ).toISOString();
              delete copyOfData.timestamp_dt;
            }
          } catch (error) {
            delete copyOfData.timestamp_dt;
          }

          if (copyOfData.subevent && !copyOfData.sub_event) {
            copyOfData.sub_event = copyOfData.subevent;
            delete copyOfData.subevent;
          } else if (copyOfData.sub_event && copyOfData.subevent) {
            delete copyOfData.subevent;
          }

          delete copyOfData.mission_id;
          delete copyOfData.mxs_gold_claimed;

          copyOfDatas.push(copyOfData);
        }

        await upsertData("sys_audit", copyOfDatas, "id");
      } else {
        const copyOfData = { ...data };
        delete copyOfData.date_timestamp;
        delete copyOfData.firebase_timestamp;

        if (!copyOfData.id) {
          copyOfData.id = docId;
        }

        try {
          if (copyOfData.timestamp_dt && !copyOfData.created_at) {
            copyOfData.created_at = new Date(
              copyOfData.timestamp_dt * 1000
            ).toISOString();
            delete copyOfData.timestamp_dt;
          }
        } catch (error) {
          delete copyOfData.timestamp_dt;
        }

        delete copyOfData.mission_id;
        delete copyOfData.mxs_gold_claimed;

        await upsertData("sys_audit", copyOfData, "id");
      }
    } catch (error: any) {
      console.log("Error adding audit log to Supabase: ", error);
      throw new Error("Error adding audit log to Supabase: " + error.message);
    }
  } catch (error: any) {
    console.log("Error adding audit log: ", error);
    throw new Error("Error adding audit log: " + error.message);
  }
}

export const getNodesForUser = async (userId: any): Promise<any | null> => {
  try {
    let lastDoc = null;

    let userAuditQuery = query(
      collection(db, "node_purchased"),
      where("uid", "==", userId),
      orderBy("purchase_date_time", "desc"),
      limit(2)
    );

    if (lastDoc) {
      userAuditQuery = query(
        collection(db, "node_purchased"),
        where("uid", "==", userId),
        orderBy("purchase_date_time", "desc"),
        startAfter(lastDoc),
        limit(2)
      );
    }

    const querySnapshot = await getDocs(userAuditQuery);

    if (!querySnapshot.empty) {
      lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
    }

    const nodesData = querySnapshot.docs.map((doc) => doc.data());
    return nodesData;
  } catch (error) {
    console.error("Error fetching audit table documents: ", error);
    console.log(error);
    return null;
  }
};

export const addToTransactions = async (
  details: TransactionsParams
): Promise<void> => {
  try {
    const docData = {
      purchase_trx: details.purchase_trx,
      trx_source_wallet: details.trx_source_wallet,
      trx_dest_wallet: details.trx_dest_wallet,
      trx_amount: details.trx_amount,
      trx_chain: details.trx_chain,
      trx_coin: details.trx_coin,
      trx_status: details.trx_status,
      trx_date_time: serverTimestamp(),
      trx_type: details.trx_type,
      trx_uid: details.trx_uid,
      trx_comment: details.trx_comment,
    };

    const collectionRef = collection(db, "transactions_pending");

    await addDoc(collectionRef, docData);
  } catch (error) {
    console.error("Error adding document to audit table: ", error);
  }
};